import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getGitHubStats } from '../../../services/githubService';

const GitHubActivity = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('week');

  // User-provided canonical GitHub summary — use these for the main summary to avoid mixed/mock values
  const githubSummary = {
    username: 'Mustan-dev',
    totalCommits: 217,
    totalContributions: 247,
    totalRepos: 23,
    visitors: 193,
    following: 3,
    contributionStreak: 2,
    status: 'active'
  };

  const [liveData, setLiveData] = useState(null);
  const [languageStats, setLanguageStats] = useState([
    { name: 'JavaScript', percentage: 35, color: 'bg-yellow-500' },
    { name: 'TypeScript', percentage: 28, color: 'bg-blue-500' },
    { name: 'Python', percentage: 20, color: 'bg-green-500' },
    { name: 'Go', percentage: 10, color: 'bg-cyan-500' },
    { name: 'Other', percentage: 7, color: 'bg-slate-500' }
  ]);

  useEffect(() => {
    let mounted = true;
    async function fetchGitHub() {
      try {
        const data = await getGitHubStats(githubSummary.username);
        if (!mounted) return;
        if (!data) {
          // No token or no data — keep canonical summary
          return;
        }

        // Map GraphQL response to the UI shape used here
        const mapped = {
          username: data.login || githubSummary.username,
          totalCommits: data.contributionsCollection?.totalCommitContributions ?? githubSummary.totalCommits,
          totalContributions: data.contributionsCollection?.contributionCalendar?.totalContributions ?? githubSummary.totalContributions,
          totalRepos: data.repositories?.totalCount ?? githubSummary.totalRepos,
          followers: data.followers?.totalCount ?? null,
          following: data.following?.totalCount ?? githubSummary.following,
          repositories: (data.repositories?.nodes || []).slice(0, 8).map((r) => ({
            name: r.name,
            description: r.description || '',
            language: r.primaryLanguage?.name || 'N/A',
            stars: r.stargazerCount || 0,
            forks: r.forkCount || 0,
            url: r.url,
            lastCommit: r.pushedAt ? new Date(r.pushedAt).toLocaleDateString() : 'N/A',
            status: 'active'
          }))
        };

        setLiveData(mapped);

        // Calculate real language distribution from all repos
        // Count repositories by language (not by lines of code, but by repo count)
        const langCounts = {};
        (data.repositories?.nodes || []).forEach((repo) => {
          const lang = repo.primaryLanguage?.name;
          if (lang) {
            langCounts[lang] = (langCounts[lang] || 0) + 1;
          }
        });

        // Calculate percentages based on repo count
        const totalRepos = Object.values(langCounts).reduce((sum, count) => sum + count, 0);
        
        const langColors = {
          'Jupyter Notebook': 'bg-orange-500',
          'TypeScript': 'bg-blue-500',
          'JavaScript': 'bg-yellow-500',
          'Java': 'bg-red-500',
          'C#': 'bg-purple-600',
          'Python': 'bg-blue-400',
          'C': 'bg-blue-600',
          'C++': 'bg-pink-500',
          'Go': 'bg-cyan-500',
          'Rust': 'bg-orange-500',
          'Ruby': 'bg-red-400',
          'PHP': 'bg-purple-500',
          'Swift': 'bg-orange-400',
          'Kotlin': 'bg-purple-400',
          'Dart': 'bg-blue-400',
          'HTML': 'bg-orange-600',
          'CSS': 'bg-blue-300',
          'Shell': 'bg-green-600',
        };

        const langStatsArray = Object.entries(langCounts)
          .map(([name, count]) => ({
            name,
            count,
            percentage: Math.round((count / totalRepos) * 100),
            color: langColors[name] || 'bg-slate-500'
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6); // Show top 6 languages

        if (langStatsArray.length > 0) {
          setLanguageStats(langStatsArray);
        }
      } catch (err) {
        // Don't break the UI if the token is missing or request fails
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch GitHub stats:', err.message || err);
      }
    }

    fetchGitHub();
    return () => { mounted = false; };
  }, []);

  const activityData = {
    week: {
      commits: 23,
      repositories: 4,
      languages: ['JavaScript', 'TypeScript', 'Python'],
      streak: 5,
      contributions: [
        { date: '2024-10-29', count: 8, day: 'Today' },
        { date: '2024-10-28', count: 5, day: 'Yesterday' },
        { date: '2024-10-27', count: 3, day: 'Sun' },
        { date: '2024-10-26', count: 4, day: 'Sat' },
        { date: '2024-10-25', count: 2, day: 'Fri' },
        { date: '2024-10-24', count: 1, day: 'Thu' },
        { date: '2024-10-23', count: 0, day: 'Wed' }
      ]
    },
    month: {
      commits: 127,
      repositories: 8,
      languages: ['JavaScript', 'TypeScript', 'Python', 'Go'],
      streak: 18,
      contributions: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(2024, 9, 30 - i)?.toISOString()?.split('T')?.[0],
        count: Math.floor(Math.random() * 10),
        day: new Date(2024, 9, 30 - i)?.toLocaleDateString('en', { weekday: 'short' })
      }))?.reverse()
    },
    year: {
      commits: 1456,
      repositories: 24,
      languages: ['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust'],
      streak: 89,
      contributions: Array.from({ length: 365 }, (_, i) => ({
        date: new Date(2024, 0, 1 + i)?.toISOString()?.split('T')?.[0],
        count: Math.floor(Math.random() * 15),
        day: new Date(2024, 0, 1 + i)?.toLocaleDateString('en', { weekday: 'short' })
      }))
    }
  };

  const recentRepositories = [
    {
      name: 'portfolio-website',
      description: 'Personal portfolio built with React and Three.js',
      language: 'TypeScript',
      stars: 24,
      forks: 8,
      lastCommit: '2 hours ago',
      status: 'active'
    },
    {
      name: 'microservices-api',
      description: 'Scalable microservices architecture with Node.js',
      language: 'JavaScript',
      stars: 45,
      forks: 12,
      lastCommit: '1 day ago',
      status: 'active'
    },
    {
      name: 'ml-data-pipeline',
      description: 'Machine learning data processing pipeline',
      language: 'Python',
      stars: 18,
      forks: 5,
      lastCommit: '3 days ago',
      status: 'maintenance'
    },
    {
      name: 'react-component-library',
      description: 'Reusable React components with Storybook',
      language: 'TypeScript',
      stars: 67,
      forks: 23,
      lastCommit: '1 week ago',
      status: 'stable'
    }
  ];

  const currentData = activityData?.[selectedPeriod];

  // Normalize week contributions to strict Mon -> Sun order using the date field
  const weekOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekContributionsSorted = selectedPeriod === 'week'
    ? (currentData?.contributions ?? [])
        .map((d) => {
          const dt = d?.date ? new Date(d.date) : null;
          const fullLabel = dt ? dt.toLocaleDateString('en', { weekday: 'long' }) : (d?.day || '');
          const short3 = fullLabel?.slice(0, 3);
          const one = fullLabel?.charAt(0);
          const weekIndex = weekOrder.indexOf(short3);
          return { ...d, weekLabelFull: fullLabel, weekLabelShort3: short3, weekLabelOne: one, weekIndex: weekIndex === -1 ? 99 : weekIndex };
        })
        .sort((a, b) => a.weekIndex - b.weekIndex)
    : [];

  const getContributionIntensity = (count) => {
    if (count === 0) return 'bg-slate-100';
    if (count <= 2) return 'bg-emerald-200';
    if (count <= 5) return 'bg-emerald-400';
    if (count <= 8) return 'bg-emerald-600';
    return 'bg-emerald-800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500';
      case 'maintenance': return 'bg-amber-500';
      case 'stable': return 'bg-blue-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <div className="bg-card border-2 border-border rounded-xl p-8 lg:p-12 pl-2 sm:pl-3 md:pl-4 lg:pl-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-xl font-bold text-foreground">GitHub Activity</h3>
          <p className="text-muted-foreground">Real-time development activity and contributions</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Github" size={20} color="var(--color-primary)" />
          <a className="text-sm text-muted-foreground hover:underline" href={`https://github.com/${githubSummary.username}`} target="_blank" rel="noopener noreferrer">@{githubSummary.username}</a>
        </div>
      </div>

      <div className="flex space-x-2 mb-10">
        {['week', 'month', 'year']?.map((period) => (
          <button key={period} onClick={() => setSelectedPeriod(period)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedPeriod === period ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
            {period?.charAt(0)?.toUpperCase() + period?.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
  <div className="space-y-6 lg:col-span-1 lg:-ml-2 xl:-ml-4">
          <h4 className="font-semibold text-foreground">Activity Summary</h4>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-foreground">{liveData?.totalCommits ?? githubSummary.totalCommits}</div>
                <div className="text-sm text-muted-foreground">Total Commits</div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-foreground">{liveData?.totalRepos ?? githubSummary.totalRepos}</div>
                <div className="text-sm text-muted-foreground">Repositories</div>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-foreground">{liveData?.totalContributions ?? githubSummary.totalContributions}</div>
                <div className="text-sm text-muted-foreground">Contributions</div>
              </div>
            </div>

          <div>
            <h5 className="font-medium text-foreground mb-3">Language Distribution</h5>
            <div className="space-y-2">
              {languageStats?.map((lang, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${lang?.color}`} />
                    <span className="text-sm text-foreground">{lang?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {lang?.count && <span className="text-xs text-muted-foreground">({lang?.count})</span>}
                    <span className="text-sm font-medium text-foreground">{lang?.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 lg:pl-8 xl:pl-12">
          <h4 className="font-semibold text-foreground mb-4">Contribution Graph</h4>

          {selectedPeriod === 'week' && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-x-4 mb-2">
                  {weekContributionsSorted?.map((day, index) => (
                    <div key={`label-${index}`} className="text-center text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                      {day?.weekLabelShort3}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-x-4 gap-y-3">
                  {weekContributionsSorted?.map((day, index) => (
                    <div key={`bar-${index}`} className="text-center">
                      <div className={`w-full h-12 md:h-14 lg:h-16 rounded ${getContributionIntensity(day?.count)} flex items-center justify-center text-sm md:text-base font-medium ${day?.count > 5 ? 'text-white' : 'text-slate-700'}`} title={`${day?.weekLabel}: ${day?.count} contributions`}>{day?.count}</div>
                    </div>
                  ))}
                </div>
              </>
          )}

          {selectedPeriod === 'month' && (
            <div className="grid grid-cols-[repeat(10,minmax(0,1fr))] sm:grid-cols-[repeat(12,minmax(0,1fr))] md:grid-cols-[repeat(15,minmax(0,1fr))] gap-2 md:gap-3 lg:max-w-[900px] lg:ml-auto">
              {currentData?.contributions?.map((day, index) => (
                <div key={index} className={`w-4 h-4 md:w-6 md:h-6 lg:w-7 lg:h-7 rounded-sm ${getContributionIntensity(day?.count)} cursor-pointer hover:scale-125 transition-transform`} title={`${day?.date}: ${day?.count} contributions`} />
              ))}
            </div>
          )}

          {selectedPeriod === 'year' && (
            <div className="grid grid-cols-[repeat(26,minmax(0,1fr))] sm:grid-cols-[repeat(36,minmax(0,1fr))] md:grid-cols-[repeat(52,minmax(0,1fr))] gap-1 md:gap-1.5 lg:max-w-[900px] lg:ml-auto">
              {currentData?.contributions?.map((day, index) => (
                <div key={index} className={`w-2.5 h-2.5 md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 rounded-sm ${getContributionIntensity(day?.count)} cursor-pointer hover:scale-150 transition-transform`} title={`${day?.date}: ${day?.count} contributions`} />
              ))}
            </div>
          )}

          <div className="mt-8">
            <h4 className="font-semibold text-foreground mb-4">Recent Repositories</h4>
            <div className="space-y-3">
              {(liveData?.repositories ?? recentRepositories)?.map((repo, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h5 className="font-medium text-foreground">{repo?.name}</h5>
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(repo?.status)}`} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{repo?.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span className="flex items-center space-x-1"><Icon name="Circle" size={8} color="var(--color-primary)" /><span>{repo?.language}</span></span>
                      <span className="flex items-center space-x-1"><Icon name="Star" size={12} /><span>{repo?.stars}</span></span>
                      <span className="flex items-center space-x-1"><Icon name="GitFork" size={12} /><span>{repo?.forks}</span></span>
                      <span>{repo?.lastCommit}</span>
                    </div>
                  </div>
                  {repo?.url ? <a href={repo.url} target="_blank" rel="noopener noreferrer"><Icon name="ExternalLink" size={16} color="var(--color-muted-foreground)" /></a> : <Icon name="ExternalLink" size={16} color="var(--color-muted-foreground)" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GitHubActivity;
